import React, { useState, useId } from 'react';
import { joinWaitlist } from '../../lib/api/waitlist';
import './WaitlistForm.css';

type FormState = 'idle' | 'submitting' | 'success' | 'error-invalid' | 'error-network';

export default function WaitlistForm() {
  const [email, setEmail] = useState('');
  const [formState, setFormState] = useState<FormState>('idle');
  const [successEmail, setSuccessEmail] = useState('');
  const inputId = useId();
  const helperId = useId();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formState === 'submitting') return;

    const trimmed = email.trim();
    if (!trimmed) {
      setFormState('error-invalid');
      return;
    }

    setFormState('submitting');

    try {
      await joinWaitlist(trimmed);
      setSuccessEmail(trimmed);
      setFormState('success');
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      if (message.includes("valid email")) {
        setFormState('error-invalid');
      } else {
        setFormState('error-network');
      }
    }
  };

  const helperText = {
    idle: 'One email when early access opens.',
    submitting: 'One email when early access opens.',
    success: "Thank you. We'll be in touch.",
    'error-invalid': "That doesn't look like a valid email.",
    'error-network': "Something didn't work. Try again in a moment.",
  }[formState];

  return (
    <section className="waitlist landing__section" aria-labelledby="waitlist-heading">
      <div className="landing__column landing__column--narrow">
        <div className="fade-up">
          <h2 className="waitlist__heading" id="waitlist-heading">
            We're building this carefully.
          </h2>
          <p className="waitlist__lede">
            Leave your email and we'll write to you when it's ready.
          </p>
          <p className="waitlist__promise">
            One email when early access opens. No drip campaigns, no marketing,
            no "just checking in."
          </p>

          {formState === 'success' ? (
            <div className="waitlist__success" role="status">
              <span className="waitlist__success-check" aria-hidden="true">✓</span>
              <span className="waitlist__success-email">{successEmail}</span>
            </div>
          ) : (
            <form
              className="waitlist__form"
              onSubmit={handleSubmit}
              noValidate
              aria-label="Join the waitlist"
            >
              <div className="waitlist__field">
                <label htmlFor={inputId} className="waitlist__label">
                  Your email
                </label>
                <input
                  id={inputId}
                  type="email"
                  className={`waitlist__input${formState === 'error-invalid' ? ' waitlist__input--error' : ''}`}
                  placeholder="you@somewhere.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={formState === 'submitting'}
                  aria-describedby={helperId}
                  aria-invalid={formState === 'error-invalid'}
                  autoComplete="email"
                />
              </div>

              <button
                type="submit"
                className="waitlist__button"
                disabled={formState === 'submitting'}
              >
                {formState === 'submitting' ? (
                  <>
                    <span className="waitlist__spinner" aria-hidden="true" />
                    Joining…
                  </>
                ) : (
                  'Join the waitlist'
                )}
              </button>

              <p
                id={helperId}
                className={`waitlist__helper${formState.startsWith('error') ? ' waitlist__helper--error' : ''}`}
                aria-live="polite"
              >
                {helperText}
              </p>

              <p className="waitlist__consent">
                By submitting, you agree to receive one email when early access
                opens. We don't sell or share your email. See our{' '}
                <a href="/privacy" className="waitlist__consent-link">privacy policy</a>{' '}
                for details.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
